import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Image from '../AppImage';
import Button from './Button';
import { useTheme, THEME_MODES } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

const ThemeToggle = () => {
  const { themeMode, setTheme, resolvedTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref?.current && !ref?.current?.contains(e?.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const options = [
    { mode: THEME_MODES?.LIGHT, label: 'Light', icon: 'Sun' },
    { mode: THEME_MODES?.DARK, label: 'Dark', icon: 'Moon' },
    { mode: THEME_MODES?.SYSTEM, label: 'System', icon: 'Monitor' },
  ];

  const currentIcon = themeMode === THEME_MODES?.SYSTEM
    ? 'Monitor'
    : resolvedTheme === 'dark' ? 'Moon' : 'Sun';

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="icon"
        className="h-10 w-10"
        onClick={() => setOpen(!open)}
        aria-label="Toggle theme"
      >
        <Icon name={currentIcon} size={20} />
      </Button>
      {open && (
        <div className="absolute right-0 mt-1 w-36 bg-popover border border-border rounded-md shadow-dropdown z-200 animate-fade-in">
          {options?.map((opt) => (
            <button
              key={opt?.mode}
              onClick={() => { setTheme(opt?.mode); setOpen(false); }}
              className={`flex items-center space-x-2 w-full px-3 py-2 text-sm transition-colors duration-150 first:rounded-t-md last:rounded-b-md ${
                themeMode === opt?.mode
                  ? 'bg-primary/10 text-primary font-medium' :'text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={opt?.icon} size={15} />
              <span>{opt?.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, signOut } = useAuth();

  const navigationItems = [
    {
      label: 'How It Works',
      path: '/how-it-works-selector',
      icon: 'HelpCircle',
      authRequired: true
    },
    {
      label: 'Coming Soon',
      path: '/coming-soon-landing',
      icon: 'Rocket'
    },
    {
      label: 'Create Stack',
      path: '/create-project-flow',
      icon: 'Plus',
      authRequired: true
    },
    {
      label: 'Stacks',
      path: '/all-projects',
      icon: 'FolderOpen',
      authRequired: true
    }
  ];

  const isActivePath = (path) => {
    return location?.pathname === path || location?.pathname?.startsWith(path);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login-sign-up');
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-100" style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}>
      <div className="flex items-center justify-between h-32 px-4 lg:px-6">
        {/* Logo */}
        <div className="flex items-center">
          <button
            onClick={() => handleNavigation('/all-projects')}
            className="flex items-center space-x-2 hover:opacity-80 transition-opacity duration-200"
          >
            <div className="h-40 flex items-center">
              <Image
                src="/assets/images/Stackd_logo_design_no_background_white_text-1772559212336.png"
                alt="Stackd"
                className="h-40 w-auto object-contain"
              />
            </div>
          </button>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          {navigationItems?.filter(item => !item?.authRequired || isAuthenticated)?.map((item) => (
            <button
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className={`flex items-center space-x-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActivePath(item?.path)
                  ? 'text-white shadow-sm bg-white/20'
                  : 'text-white/80 hover:text-white hover:bg-white/15'
              }`}
            >
              <Icon name={item?.icon} size={15} />
              <span>{item?.label}</span>
            </button>
          ))}
        </nav>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMobileMenu}
            className="h-10 w-10 text-white hover:bg-white/15"
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={20} />
          </Button>
        </div>

        {/* User Actions (Desktop) */}
        <div className="hidden md:flex items-center space-x-2">
          <ThemeToggle />
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 text-white hover:bg-white/15"
            onClick={() => handleNavigation('/unified-notification-center')}
          >
            <Icon name="Bell" size={20} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 text-white hover:bg-white/15"
            onClick={() => handleNavigation('/user-settings')}
          >
            <Icon name="Settings" size={20} />
          </Button>
          {isAuthenticated ? (
            <button
              onClick={handleSignOut}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-white/90 hover:text-white hover:bg-white/15 transition-all duration-200 text-sm font-medium"
            >
              <Icon name="LogOut" size={15} color="white" />
              <span>Sign Out</span>
            </button>
          ) : (
            <button
              onClick={() => handleNavigation('/login-sign-up')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/20 hover:bg-white/30 text-white transition-all duration-200 text-sm font-medium border border-white/30"
            >
              <Icon name="User" size={15} color="white" />
              <span>Sign In</span>
            </button>
          )}
        </div>
      </div>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden">
          <div className="px-3 pt-2 pb-4 space-y-1 border-t border-white/20 shadow-lg" style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}>
            {navigationItems?.filter(item => !item?.authRequired || isAuthenticated)?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`flex items-center space-x-3 w-full px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 ${
                  isActivePath(item?.path)
                    ? 'text-white bg-white/20' : 'text-white/80 hover:text-white hover:bg-white/15'
                }`}
              >
                <Icon name={item?.icon} size={20} color="white" />
                <span>{item?.label}</span>
              </button>
            ))}

            {/* Mobile User Actions */}
            <div className="pt-3 mt-3 border-t border-white/20">
              <button
                onClick={() => handleNavigation('/unified-notification-center')}
                className="flex items-center space-x-3 w-full px-4 py-3 rounded-xl text-base font-medium text-white/80 hover:text-white hover:bg-white/15 transition-all duration-200"
              >
                <Icon name="Bell" size={20} color="white" />
                <span>Notifications</span>
              </button>
              <button
                className="flex items-center space-x-3 w-full px-4 py-3 rounded-xl text-base font-medium text-white/80 hover:text-white hover:bg-white/15 transition-all duration-200"
                onClick={() => handleNavigation('/user-settings')}
              >
                <Icon name="Settings" size={20} color="white" />
                <span>Settings</span>
              </button>
              {isAuthenticated ? (
                <button
                  onClick={handleSignOut}
                  className="flex items-center space-x-3 w-full px-4 py-3 rounded-xl text-base font-medium text-white/80 hover:text-white hover:bg-white/15 transition-all duration-200"
                >
                  <Icon name="LogOut" size={20} color="white" />
                  <span>Sign Out</span>
                </button>
              ) : (
                <button
                  onClick={() => handleNavigation('/login-sign-up')}
                  className="flex items-center space-x-3 w-full px-4 py-3 rounded-xl text-base font-medium text-white/80 hover:text-white hover:bg-white/15 transition-all duration-200"
                >
                  <Icon name="User" size={20} color="white" />
                  <span>Sign In</span>
                </button>
              )}
              <div className="px-4 py-3">
                <p className="text-xs font-medium text-white/70 mb-2">Theme</p>
                <MobileThemeSelector />
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

const MobileThemeSelector = () => {
  const { themeMode, setTheme } = useTheme();
  const options = [
    { mode: THEME_MODES?.LIGHT, label: 'Light', icon: 'Sun' },
    { mode: THEME_MODES?.DARK, label: 'Dark', icon: 'Moon' },
    { mode: THEME_MODES?.SYSTEM, label: 'System', icon: 'Monitor' },
  ];
  return (
    <div className="flex space-x-2">
      {options?.map((opt) => (
        <button
          key={opt?.mode}
          onClick={() => setTheme(opt?.mode)}
          className={`flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm transition-colors duration-150 ${
            themeMode === opt?.mode
              ? 'bg-primary/10 text-primary font-medium' :'text-text-secondary hover:bg-muted'
          }`}
        >
          <Icon name={opt?.icon} size={14} />
          <span>{opt?.label}</span>
        </button>
      ))}
    </div>
  );
};

export default Header;